// apps/web/src/components/dashboard/DashboardHighlightsBand.tsx
//
// KAN-209 Briefing Desk — "Today's highlights": four editorial cells
// separated by quiet dividers (no card grid). Every figure is derived
// from real data: T1 (critical activity), the top-ranked story (lead),
// T4 (largest new fine) and T6 (supply-chain incidents). A cell that
// cannot be backed by data degrades to skeleton / quiet-unavailable /
// conservative copy — never an invented number.
//
// Each populated cell is a §3.1.1-compliant <article>: exactly one
// stretched <Link>, no buttons.

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Activity, Link2, Newspaper, Scale } from 'lucide-react'
import { useDashboardTile } from '@/hooks/useDashboardTile'
import { formatEur } from '@/lib/format-eur'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { TrendDelta } from './TrendDelta'
import type { StoryCardShape } from '@/types/story'

interface T1Response {
  count24h: number
  sevenDayAvg: number
  delta: number
}
interface T4Response {
  count: number
  totalAmountEur: number
  deltaEur: number
  largest: { organisation: string; amountEur: number } | null
}
interface T6Response {
  count: number
  delta: number
  breakdown: { critical: number; high: number; medium: number; low: number }
}

const KICKER_CLASSES = {
  critical: 'text-rose-700 dark:text-rose-300',
  high: 'text-amber-700 dark:text-amber-300',
  medium: 'text-sky-700 dark:text-sky-300',
  neutral: 'text-muted-foreground',
} as const

type KickerTone = keyof typeof KICKER_CLASSES

function HighlightCellShell(props: { children: ReactNode }) {
  return <div className="min-w-0 bg-background p-4 sm:p-5">{props.children}</div>
}

function HighlightSkeleton(props: { label: string }) {
  return (
    <HighlightCellShell>
      <div role="status" aria-label={`Loading ${props.label}`}>
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-3 h-4 w-full" />
        <Skeleton className="mt-1.5 h-4 w-3/4" />
        <Skeleton className="mt-3 h-3 w-28" />
      </div>
    </HighlightCellShell>
  )
}

function HighlightUnavailable(props: { kicker: string }) {
  return (
    <HighlightCellShell>
      <HighlightKicker tone="neutral" icon={null} label={props.kicker} />
      <p className="mt-2 text-sm text-muted-foreground">
        Unavailable right now.
      </p>
    </HighlightCellShell>
  )
}

function HighlightKicker(props: {
  tone: KickerTone
  icon: ReactNode
  label: string
}) {
  return (
    <span
      className={cn(
        'flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.14em]',
        KICKER_CLASSES[props.tone],
      )}
    >
      {props.icon}
      {props.label}
    </span>
  )
}

function HighlightCard(props: {
  tone: KickerTone
  icon: ReactNode
  kicker: string
  title: string
  to: string
  meta: ReactNode
}) {
  return (
    <article className="group relative min-w-0 bg-background p-4 transition-colors hover:bg-secondary focus-within:bg-secondary sm:p-5">
      <HighlightKicker tone={props.tone} icon={props.icon} label={props.kicker} />
      <h3 className="mt-2 line-clamp-3 font-heading text-[15px] font-semibold leading-snug">
        <Link
          to={props.to}
          className="after:absolute after:inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:text-primary"
        >
          {props.title}
        </Link>
      </h3>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        {props.meta}
      </div>
    </article>
  )
}

function CriticalActivityCell() {
  const { data, isLoading, isError } = useDashboardTile<T1Response>('criticals-24h')
  if (isLoading) return <HighlightSkeleton label="critical activity" />
  if (isError || !data) return <HighlightUnavailable kicker="Critical activity" />
  return (
    <HighlightCard
      tone="critical"
      icon={<Activity className="h-3 w-3" aria-hidden="true" />}
      kicker="Critical activity"
      title={`${data.count24h.toLocaleString('en-GB')} critical events in the last 24 hours`}
      to="/stories?severity=critical&since=24h"
      meta={
        <>
          <TrendDelta value={data.delta} polarity="bad-up" />
          <span>vs 7-day average ({data.sevenDayAvg.toLocaleString('en-GB')})</span>
        </>
      }
    />
  )
}

function LeadStoryCell(props: {
  stories: StoryCardShape[] | undefined
  isLoading?: boolean
  isError?: boolean
}) {
  if (props.isLoading) return <HighlightSkeleton label="lead story" />
  if (props.isError) return <HighlightUnavailable kicker="Lead story" />
  const lead = [...(props.stories ?? [])].sort(
    (a, b) => b.maxRiskScore - a.maxRiskScore,
  )[0]
  if (!lead) {
    return (
      <HighlightCellShell>
        <HighlightKicker
          tone="neutral"
          icon={<Newspaper className="h-3 w-3" aria-hidden="true" />}
          label="Lead story"
        />
        <p className="mt-2 text-sm text-muted-foreground">No lead story yet.</p>
      </HighlightCellShell>
    )
  }
  return (
    <HighlightCard
      tone={lead.maxSeverity === 'critical' ? 'critical' : 'high'}
      icon={<Newspaper className="h-3 w-3" aria-hidden="true" />}
      kicker="Lead story"
      title={lead.title}
      to={`/stories/${lead.id}`}
      meta={
        <>
          <span className="font-medium text-foreground">
            {lead.publisherBadge.name}
          </span>
          <span>·</span>
          <span>{lead.sourceCount} sources</span>
          <span>·</span>
          <span>
            {formatDistanceToNow(new Date(lead.latestSourceAddedAt), {
              addSuffix: true,
            })}
          </span>
        </>
      }
    />
  )
}

function EnforcementCell() {
  const { data, isLoading, isError } = useDashboardTile<T4Response>('new-fines-30d')
  if (isLoading) return <HighlightSkeleton label="new enforcement" />
  if (isError || !data) return <HighlightUnavailable kicker="New enforcement" />
  const title = data.largest
    ? `${data.largest.organisation} fined ${formatEur(data.largest.amountEur)}`
    : data.count > 0
      ? `${data.count.toLocaleString('en-GB')} new fines in the last 30 days`
      : 'No new fines in the last 30 days'
  return (
    <HighlightCard
      tone="medium"
      icon={<Scale className="h-3 w-3" aria-hidden="true" />}
      kicker="New enforcement"
      title={title}
      to="/regulatory-actions?since=30d"
      meta={
        data.count > 0 ? (
          <span>
            {data.count.toLocaleString('en-GB')} fines ·{' '}
            {formatEur(data.totalAmountEur)} total
          </span>
        ) : (
          <span>Across monitored frameworks</span>
        )
      }
    />
  )
}

function SupplyChainCell() {
  const { data, isLoading, isError } = useDashboardTile<T6Response>('supply-chain-7d')
  if (isLoading) return <HighlightSkeleton label="supply chain" />
  if (isError || !data) return <HighlightUnavailable kicker="Supply chain" />
  return (
    <HighlightCard
      tone="high"
      icon={<Link2 className="h-3 w-3" aria-hidden="true" />}
      kicker="Supply chain"
      title={`${data.count.toLocaleString('en-GB')} supply-chain incidents in 7 days`}
      to="/threats/supply-chain?since=7d"
      meta={
        <>
          <TrendDelta value={data.delta} polarity="bad-up" />
          <span>
            {data.breakdown.critical} critical · {data.breakdown.high} high
          </span>
        </>
      }
    />
  )
}

export interface DashboardHighlightsBandProps {
  stories: StoryCardShape[] | undefined
  storiesLoading?: boolean
  storiesError?: boolean
}

export function DashboardHighlightsBand(props: DashboardHighlightsBandProps) {
  return (
    <div className="grid grid-cols-1 gap-px border-y border-border bg-border sm:grid-cols-2 xl:grid-cols-4">
      <CriticalActivityCell />
      <LeadStoryCell
        stories={props.stories}
        isLoading={props.storiesLoading}
        isError={props.storiesError}
      />
      <EnforcementCell />
      <SupplyChainCell />
    </div>
  )
}
